import { Button, Icon, type ToastType } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  crmPackageMappings,
  type CrmPackageMapping,
} from "../../api/crm-package-mappings.service.js";
import { subscriptions } from "../../api/subscriptions.service.js";
import { CrmPackageMappingDeleteModal } from "../../components/CrmPackageMappingDeleteModal/index.js";
import { CrmPackageMappingSkipWarningModal } from "../../components/CrmPackageMappingSkipWarningModal/index.js";
import { CrmPackageMappingForm } from "../../components/CrmPackageMappingForm/index.js";
import { UnauthorizedView } from "../../components/UnauthorizedView/index.js";
import EmptyState from "../../components/EmptyState/index.js";
import LoadingState from "../../components/LoadingState/index.js";
import type { CrmPackageMappingFormData } from "../../schemas/crmPackageMappingSchema.js";
import { formatDateShort } from "../../utils/dateFormatter.js";
import styles from "./CrmPackageMappingDetail.module.css";

type CrmPackageMappingDetailContext = {
  toast: { message: string; type: ToastType } | null;
  setToast: (toast: { message: string; type: ToastType } | null) => void;
  onMutationSuccess?: () => void;
  setIsEditMode?: (isEditMode: boolean) => void;
};

function CrmPackageMappingDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    setToast,
    onMutationSuccess,
    setIsEditMode: setParentEditMode,
  } = useOutletContext<CrmPackageMappingDetailContext>();
  const [mapping, setMapping] = useState<CrmPackageMapping | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSkipWarningModalOpen, setIsSkipWarningModalOpen] = useState(false);
  const [activeSubscriptionCount, setActiveSubscriptionCount] = useState(0);
  const [pendingSaveData, setPendingSaveData] = useState<CrmPackageMappingFormData | null>(null);

  useEffect(() => {
    if (id) {
      loadMapping(id);
    }
  }, [id]);

  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (setParentEditMode) {
      setParentEditMode(isEditMode);
    }
  }, [isEditMode, setParentEditMode]);

  useEffect(() => {
    return () => {
      if (setParentEditMode) {
        setParentEditMode(false);
      }
    };
  }, [setParentEditMode]);

  async function loadMapping(mappingId: string) {
    try {
      setLoading(true);
      setError(null);
      const mappingData = await crmPackageMappings.getById(mappingId);
      setMapping(mappingData);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load mapping"));
    } finally {
      setLoading(false);
    }
  }

  function handleEditClick() {
    setIsEditMode(true);
  }

  function handleCancelEdit() {
    if (isDirty) {
      const confirmDiscard = window.confirm(
        "You have unsaved changes. Are you sure you want to discard them?",
      );
      if (!confirmDiscard) {
        return;
      }
    }
    setIsEditMode(false);
    setIsDirty(false);
  }

  async function handleSave(data: CrmPackageMappingFormData) {
    if (!id || !mapping) return;

    const isChangingToSkip = mapping.action === "map" && data.action === "skip";

    if (isChangingToSkip && mapping.package?.productCode) {
      try {
        const count = await subscriptions.getActiveCountByProductCode(mapping.package.productCode);
        if (count > 0) {
          setActiveSubscriptionCount(count);
          setPendingSaveData(data);
          setIsSkipWarningModalOpen(true);
          return;
        }
      } catch (err) {
        setToast({
          message: `Failed to check active subscriptions: ${err instanceof Error ? err.message : "Unknown error"}`,
          type: "error",
        });
      }
    }

    await performSave(data);
  }

  async function performSave(data: CrmPackageMappingFormData) {
    if (!id) return;

    setIsSubmitting(true);
    try {
      const updatedMapping = await crmPackageMappings.update(id, {
        crmPackageId: data.crmPackageId,
        action: data.action,
        packageId: data.action === "skip" ? null : data.packageId,
        description: data.description || null,
      });
      setMapping(updatedMapping);
      setIsEditMode(false);
      setIsDirty(false);
      setToast({
        message: `Mapping "${updatedMapping.crmPackageId}" updated successfully`,
        type: "success",
      });
      onMutationSuccess?.();
    } catch (err) {
      setToast({
        message: `Failed to update mapping: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCloseSkipWarningModal() {
    if (!isSubmitting) {
      setIsSkipWarningModalOpen(false);
      setPendingSaveData(null);
      setActiveSubscriptionCount(0);
    }
  }

  async function handleSkipWarningConfirm() {
    if (!pendingSaveData) return;
    await performSave(pendingSaveData);
    setIsSkipWarningModalOpen(false);
    setPendingSaveData(null);
    setActiveSubscriptionCount(0);
  }

  function handleDeleteClick() {
    setIsDeleteModalOpen(true);
  }

  function handleCloseDeleteModal() {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!mapping) return;

    setIsDeleting(true);
    try {
      await crmPackageMappings.delete(mapping.id);
      setIsDeleteModalOpen(false);
      setToast({
        message: `Mapping "${mapping.crmPackageId}" deleted successfully`,
        type: "success",
      });
      onMutationSuccess?.();
      navigate("/fulfilment/crm-package-mappings");
    } catch (err) {
      setToast({
        message: `Failed to delete mapping: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  }

  function handleDirtyChange(dirty: boolean) {
    setIsDirty(dirty);
  }

  if (loading) {
    return <LoadingState message="Loading mapping..." />;
  }

  if (error || !mapping) {
    const status = error && (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;

    if (isAuthError) {
      return <UnauthorizedView />;
    }

    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title="Failed to load mapping"
          message={error?.message || "Mapping not found"}
          action={{
            label: "Back to Mappings",
            onClick: () => navigate("/fulfilment/crm-package-mappings"),
          }}
        />
      </div>
    );
  }

  if (isEditMode) {
    return (
      <>
        <div className={styles.header}>
          <h1 className={styles.title}>Edit Mapping</h1>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleCancelEdit}
            aria-label="Back to mapping detail"
          >
            <Icon icon="arrow-left" size="small" />
            <span>Back</span>
          </button>
        </div>

        <div className={styles.formContainer}>
          <CrmPackageMappingForm
            mode="edit"
            initialValues={mapping}
            onSubmit={handleSave}
            onCancel={handleCancelEdit}
            submitLabel="Save Changes"
            isSubmitting={isSubmitting}
            onDirtyChange={handleDirtyChange}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h1 className={styles.mappingTitle}>{mapping.crmPackageId}</h1>
        </div>
        <div className={styles.headerActions}>
          <Button variant="outlined" color="error" onClick={handleDeleteClick}>
            Delete
          </Button>
          <Button variant="contained" color="primary" onClick={handleEditClick}>
            Edit
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Mapping Information</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>CRM Package ID</span>
              <span className={styles.infoValueMono}>{mapping.crmPackageId}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Action</span>
              <span className={styles.infoValue} style={{ textTransform: "uppercase" }}>
                {mapping.action}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Package</span>
              <span className={styles.infoValue}>
                {mapping.package ? (
                  `${mapping.package.name} (${mapping.package.productCode})`
                ) : (
                  <span className={styles.emptyText}>— (skip)</span>
                )}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Description</span>
              <span className={styles.infoValue}>
                {mapping.description || <span className={styles.emptyText}>No description</span>}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Created</span>
              <span className={styles.infoValue}>{formatDateShort(mapping.createdAt)}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Updated</span>
              <span className={styles.infoValue}>{formatDateShort(mapping.updatedAt)}</span>
            </div>
          </div>
        </div>
      </div>

      <CrmPackageMappingDeleteModal
        mapping={mapping}
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />

      <CrmPackageMappingSkipWarningModal
        activeSubscriptionCount={activeSubscriptionCount}
        isOpen={isSkipWarningModalOpen}
        onClose={handleCloseSkipWarningModal}
        onConfirm={handleSkipWarningConfirm}
        isSubmitting={isSubmitting}
      />
    </>
  );
}

export default CrmPackageMappingDetail;
